import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api } from './api'
import Button from './Button.jsx'
import ConfirmDialog from './ConfirmDialog.jsx'

/**
 * The pages and profiles a business publishes to. Without one of these a post has nowhere to go,
 * so this card says plainly when the list is empty and why.
 */
export default function Accounts({ facebookEnabled }) {
  const { t } = useTranslation()
  const [accounts, setAccounts] = useState([])
  const [available, setAvailable] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  // Disconnecting stops everything scheduled for that page from going out. That is worth asking
  // about, and the platform never uses the browser's own confirm().
  const [pendingDisconnect, setPendingDisconnect] = useState(null)

  const load = useCallback(async () => {
    try {
      setAccounts(await api.socialAccounts())
    } catch (failure) {
      setError(failure.message)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const [working, setWorking] = useState(null)

  async function run(action, as = 'busy') {
    setBusy(true)
    setWorking(as)
    setError(null)
    try {
      await action()
    } catch (failure) {
      setError(failure.message)
    } finally {
      setBusy(false)
      setWorking(null)
    }
  }

  return (
    <section className="card">
      <h2>{t('accounts.title')}</h2>
      <p className="muted">{t('accounts.help')}</p>

      {!facebookEnabled && <p className="muted small">{t('accounts.facebookMissing')}</p>}

      {accounts.length === 0 && <p className="status status--bad">{t('accounts.none')}</p>}

      <ConfirmDialog
        open={pendingDisconnect !== null}
        title={t('accounts.disconnectTitle')}
        body={t('accounts.disconnectBody', { name: pendingDisconnect?.name })}
        confirmLabel={t('accounts.disconnect')}
        destructive
        onCancel={() => setPendingDisconnect(null)}
        onConfirm={() => {
          const account = pendingDisconnect
          setPendingDisconnect(null)
          run(() => api.disconnectAccount(account.id).then(load), `disconnect:${account.id}`)
        }}
      />

      {accounts.map((account) => (
        <div key={account.id} className="row account">
          <span>
            {account.pictureUrl && <img src={account.pictureUrl} alt="" className="account__picture" />}
            <strong>{account.name}</strong>{' '}
            <span className="muted small">
              {t(`networks.${account.network}`)}
              {account.status !== 'Connected' && ` · ${t('accounts.needsReconnect')}`}
            </span>
          </span>

          <Button
            type="button"
            className="link link--danger"
            disabled={busy}
            loading={working === `disconnect:${account.id}`}
            onClick={() => setPendingDisconnect(account)}
          >
            {t('accounts.disconnect')}
          </Button>
        </div>
      ))}

      {facebookEnabled && (
        <div className="row row--form">
          <Button
            type="button"
            className="button button--quiet"
            disabled={busy}
            loading={working === 'list'}
            onClick={() => run(async () => setAvailable(await api.availablePages()), 'list')}
          >
            {t('accounts.list')}
          </Button>
        </div>
      )}

      {available?.length === 0 && <p className="muted small">{t('accounts.nothingToConnect')}</p>}

      {available?.map((page) => (
        <div key={page.id} className="row account">
          <span>
            <strong>{page.name}</strong>{' '}
            <span className="muted small">
              {page.instagramUsername ? `+ @${page.instagramUsername}` : t('accounts.noInstagram')}
              {!page.canPublish && ` · ${t('accounts.cannotPublish')}`}
            </span>
          </span>

          {!page.connected && page.canPublish && (
            <Button
              type="button"
              className="button"
              disabled={busy}
              loading={working === `connect:${page.id}`}
              onClick={() =>
                run(async () => {
                  await api.connectPages([page.id])
                  setAvailable(null)
                  await load()
                }, `connect:${page.id}`)
              }
            >
              {t('accounts.connect')}
            </Button>
          )}
        </div>
      ))}

      {error && <p className="status status--bad">{error}</p>}
    </section>
  )
}
